import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { customerApi } from '../services/customerApi'

interface Customer {
  id: string
  firstName: string
  lastName: string
  email?: string
  phone?: string
  pricingPercentOfRetail?: number
}

interface Business {
  id: string
  name: string
  logo?: string
}

interface PortalConfig {
  defaultLandingPage: string
  navigationLinks: {
    dashboard: { enabled: boolean; label?: string }
    jobs: { enabled: boolean; label?: string }
    estimates: { enabled: boolean; label?: string }
    invoices: { enabled: boolean; label?: string }
    products: { enabled: boolean; label?: string }
    visualizer: { enabled: boolean; label?: string }
    subscriptions: { enabled: boolean; label?: string }
    profile: { enabled: boolean; label?: string }
  }
}

interface CustomerAuthContextType {
  customer: Customer | null
  business: Business | null
  isAuthenticated: boolean
  isLoading: boolean
  permissions: string[]
  portalConfig: PortalConfig | null
  login: (token: string) => Promise<void>
  logout: () => void
  validateSession: () => Promise<boolean>
}

const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(undefined)

interface CustomerAuthProviderProps {
  children: ReactNode
}

const DEFAULT_PORTAL_CONFIG: PortalConfig = {
  defaultLandingPage: '/dashboard',
  navigationLinks: {
    dashboard: { enabled: true, label: 'Dashboard' },
    jobs: { enabled: true, label: 'My Jobs' },
    estimates: { enabled: true, label: 'Estimates' },
    invoices: { enabled: true, label: 'Invoices' },
    products: { enabled: true, label: 'Browse Products' },
    visualizer: { enabled: true, label: 'Project Visualizer' },
    subscriptions: { enabled: true, label: 'Subscriptions' },
    profile: { enabled: true, label: 'Profile' },
  },
}

export const CustomerAuthProvider: React.FC<CustomerAuthProviderProps> = ({ children }) => {
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [business, setBusiness] = useState<Business | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [permissions, setPermissions] = useState<string[]>([])
  const [portalConfig, setPortalConfig] = useState<PortalConfig | null>(DEFAULT_PORTAL_CONFIG)

  // Initialize authentication state on app load
  useEffect(() => {
    initializeAuth()
  }, [])

  const initializeAuth = async () => {
    try {
      const token = localStorage.getItem('customerToken')
      
      if (token) {
        // Validate existing session
        const isValid = await validateSession()
        if (!isValid) {
          // Clear invalid token
          localStorage.removeItem('customerToken')
        }
      }
    } catch (error) {
      console.error('Error initializing auth:', error)
      localStorage.removeItem('customerToken')
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (token: string) => {
    try {
      // Store token
      localStorage.setItem('customerToken', token)
      
      // Validate session to get customer data
      await validateSession()
    } catch (error) {
      console.error('Login error:', error)
      localStorage.removeItem('customerToken')
      throw error
    }
  }

  const logout = () => {
    localStorage.removeItem('customerToken')
    setCustomer(null)
    setBusiness(null)
    setIsAuthenticated(false)
    setPermissions([])
    setPortalConfig(DEFAULT_PORTAL_CONFIG)
  }

  const validateSession = async (): Promise<boolean> => {
    try {
      const token = localStorage.getItem('customerToken')
      if (!token) {
        return false
      }

      const response = await customerApi.validateSession()
      
      if (response.success) {
        setCustomer(response.customer)
        setBusiness(response.business)
        setPermissions(response.permissions || [])
        setPortalConfig(response.portalConfig || DEFAULT_PORTAL_CONFIG)
        setIsAuthenticated(true)
        return true
      } else {
        return false
      }
    } catch (error: any) {
      console.error('Session validation error:', error)
      
      // If session is invalid, clear auth state
      // But don't logout if we're on an estimate page with a token parameter
      // (allows token-based access to work)
      if (error.response?.status === 401) {
        const currentPath = window.location.pathname
        const hasTokenParam = window.location.search.includes('token=')
        const isEstimatePage = currentPath.includes('/estimates/')
        
        if (!(isEstimatePage && hasTokenParam)) {
          logout()
        }
      }
      
      return false
    }
  }

  const contextValue: CustomerAuthContextType = {
    customer,
    business,
    isAuthenticated,
    isLoading,
    permissions,
    portalConfig,
    login,
    logout,
    validateSession,
  }

  return (
    <CustomerAuthContext.Provider value={contextValue}>
      {children}
    </CustomerAuthContext.Provider>
  )
}

export const useCustomerAuth = (): CustomerAuthContextType => {
  const context = useContext(CustomerAuthContext)
  if (context === undefined) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider')
  }
  return context
}

// Helper hook to check specific permissions
export const useCustomerPermission = (permission: string): boolean => {
  const { permissions } = useCustomerAuth()
  return permissions.includes(permission)
}

// Helper hook to check multiple permissions
export const useCustomerPermissions = (requiredPermissions: string[]): boolean => {
  const { permissions } = useCustomerAuth()
  return requiredPermissions.every(permission => permissions.includes(permission))
}
